import React, {useEffect, useState, useMemo} from "react";
import {Listbox} from "@headlessui/react";
import InputMask from "react-input-mask";
import {useCreate} from "../stores/houseStore";
import UploadMedia from "./UploadMedia";
import {useFetchReference, useReference} from "../stores/publicStore";
import {useAuthStore} from "../stores/authStore";

const TOTAL_STEPS = 3;

const FIELD_LABELS = {
    category_id: "Категория",
    title: "Название",
    district: "Район",
    address: "Адрес",
    description: "Описание",
    price_usd: "Цена",
    area_total_m2: "Площадь",
    land_area_sotka: "Площадь участка",
    rooms: "Комнаты",
    floor: "Этаж",
    floors_total: "Всего этажей",
    year_built: "Год постройки",
    contacts: "Контакты",
    repair_type_id: "Состояние ремонта",
    apartment_series_id: "Серия",
    construction_type_id: "Материал стен",
    heating_id: "Тип отопления",
    furniture_id: "Мебелирование",
    communications: "Коммуникации",
    purpose: "Целевое назначение",
};

const translatePlainMessage = (message) => {
    if (!message) return "проверьте значение";
    if (typeof message !== "string") return "проверьте значение";

    const knownMessages = {
        "Field required": "обязательное поле",
        "Input should be a valid string": "должно быть текстом",
        "Input should be a valid integer": "должно быть целым числом",
        "Input should be a valid number": "должно быть числом",
        "Input should be a valid UUID": "неверный формат идентификатора",
        "Category was not found": "Категория не найдена",
        "Not authenticated": "Нужно войти в аккаунт",
        "Missing authorization header": "Сессия истекла. Войдите заново.",
        "Access token expired": "Сессия истекла. Войдите заново.",
        "Internal server error": "Внутренняя ошибка сервера. Попробуйте позже.",
        "Network Error": "Не удалось подключиться к серверу. Проверьте интернет и попробуйте снова.",
    };

    if (knownMessages[message]) return knownMessages[message];

    const minLengthMatch = message.match(/^String should have at least (\d+) characters$/);
    if (minLengthMatch) return `минимум ${minLengthMatch[1]} символов`;

    const maxLengthMatch = message.match(/^String should have at most (\d+) characters$/);
    if (maxLengthMatch) return `максимум ${maxLengthMatch[1]} символов`;

    const greaterThanMatch = message.match(/^Input should be greater than (.+)$/);
    if (greaterThanMatch) return `должно быть больше ${greaterThanMatch[1]}`;

    const greaterOrEqualMatch = message.match(/^Input should be greater than or equal to (.+)$/);
    if (greaterOrEqualMatch) return `должно быть не меньше ${greaterOrEqualMatch[1]}`;

    const lessThanMatch = message.match(/^Input should be less than (.+)$/);
    if (lessThanMatch) return `должно быть меньше ${lessThanMatch[1]}`;

    const lessOrEqualMatch = message.match(/^Input should be less than or equal to (.+)$/);
    if (lessOrEqualMatch) return `должно быть не больше ${lessOrEqualMatch[1]}`;

    return "проверьте значение";
};

const translateValidationError = (item) => {
    const ctx = item?.ctx || {};

    switch (item?.type) {
        case "missing":
            return "обязательное поле";
        case "string_too_short":
            return `минимум ${ctx.min_length} символов`;
        case "string_too_long":
            return `максимум ${ctx.max_length} символов`;
        case "greater_than":
            return `должно быть больше ${ctx.gt}`;
        case "greater_than_equal":
            return `должно быть не меньше ${ctx.ge}`;
        case "less_than":
            return `должно быть меньше ${ctx.lt}`;
        case "less_than_equal":
            return `должно быть не больше ${ctx.le}`;
        case "int_parsing":
        case "int_type":
            return "должно быть целым числом";
        case "float_parsing":
        case "float_type":
            return "должно быть числом";
        case "uuid_parsing":
        case "uuid_type":
            return "неверный формат идентификатора";
        case "bool_parsing":
        case "bool_type":
            return "должно быть да/нет";
        case "datetime_parsing":
        case "datetime_type":
            return "неверный формат даты";
        case "decimal_parsing":
        case "decimal_type":
            return "должно быть числом";
        default:
            return translatePlainMessage(item?.msg);
    }
};

const getCreateErrorMessage = (error) => {
    const data = error?.response?.data || error;
    const detail = data?.detail || data?.message || data;

    if (!error?.response && error?.message === "Network Error") {
        return translatePlainMessage(error.message);
    }

    if (Array.isArray(detail)) {
        return detail
            .map((item) => {
                const field = item?.loc?.[item.loc.length - 1];
                const label = FIELD_LABELS[field] || field;
                const message = translateValidationError(item);
                return label ? `${label}: ${message}` : message;
            })
            .filter(Boolean)
            .join("; ");
    }

    if (detail && typeof detail === "object") {
        const messages = Object.entries(detail).map(([field, message]) => {
            const label = FIELD_LABELS[field] || field;
            return `${label}: ${translatePlainMessage(message)}`;
        });
        if (messages.length) return messages.join("; ");
    }

    if (typeof detail === "string") return translatePlainMessage(detail);

    return "Не удалось создать объявление. Проверьте данные и попробуйте снова.";
};

const CreateHouse = () => {
    const [id, setId] = useState('');
    const [step, setStep] = useState(1);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [category, setCategory] = useState(null);
    const [heat, setHeat] = useState(null);
    const [furnit, setFurnit] = useState(null);
    const [rep, setRep] = useState(null);
    const [apartment, setApartment] = useState(null);
    const [construc, setConstruc] = useState(null);

    const [title, setTitle] = useState("");
    const [district, setDistrict] = useState("");
    const [address, setAddress] = useState("");
    const [description, setDescription] = useState("");
    const [communications, setCommunications] = useState("");
    const [purpose, setPurpose] = useState("");
    const [phone, setPhone] = useState("");
    const [price, setPrice] = useState("");
    const [area, setArea] = useState("");
    const [areas, setAreas] = useState("");
    const [floor, setFloor] = useState("");
    const [totalFloor, setTotalFloor] = useState("");
    const [rooms, setRooms] = useState("");
    const [year, setYear] = useState("");
    const reference = useReference();
    const getReference = useFetchReference();
    const create = useCreate();
    const refreshAccessToken = useAuthStore((state) => state.refreshAccessToken);

    const isLand = category?.realty_type === "land";
    const isApartment = category?.realty_type === "apartment";
    const isHouse = category?.realty_type === "house";
    const isCommercial = category?.realty_type === "commercial";
    useEffect(() => {
        const loadReference = async () => {
            let token = localStorage.getItem("access_token");
            const expiryDate = localStorage.getItem("expiry_date");
            const expiresInMs = expiryDate ? new Date(expiryDate).getTime() - Date.now() : null;

            if (expiresInMs !== null && expiresInMs <= 60 * 1000) {
                token = await refreshAccessToken();
            }

            if (token) {
                await getReference(token);
            }
        };

        void loadReference();
    }, [getReference, refreshAccessToken]);

    function validateStep(currentStep) {
        const errs = {};

        if (currentStep === 1) {
            if (!category?.id) errs.category = "Выберите категорию";
            if (!title?.trim()) errs.title = "Название обязательно";
            else if (title.trim().length < 5) errs.title = "Название должно быть не меньше 5 символов";

            if (!district?.trim()) errs.district = "Район обязателен";
            else if (district.trim().length < 5) errs.district = "Район должен быть не меньше 5 символов";

            if (!address?.trim()) errs.address = "Адрес обязателен";
            else if (address.trim().length < 5) errs.address = "Адрес должен быть не меньше 5 символов";

            if (!description?.trim()) errs.description = "Описание обязательно";
            else if (description.trim().length < 10) errs.description = "Описание должно быть не меньше 10 символов";

            if (!price || Number(price) <= 0) errs.price = "Укажите цену в $";
            if (!area || Number(area) <= 0) errs.area = "Укажите площадь м²";
            if (!phone || phone.includes("_")) errs.phone = "Укажите корректный телефон";
        }

        if (currentStep === 2) {
            if (isApartment) {
                if (!apartment?.id) errs.apartment = "Выберите серию";
                if (!construc?.id) errs.construc = "Выберите материал стен";
                if (!floor) errs.floor = "Укажите этаж";
                if (!totalFloor) errs.totalFloor = "Укажите всего этажей";
                if (!year) errs.year = "Укажите год постройки";
                if (!rep?.id) errs.rep = "Выберите состояние ремонта";
                if (!heat?.id) errs.heat = "Выберите тип отопления";
                if (!furnit?.id) errs.furnit = "Выберите тип мебелирования";
            }

            if (isCommercial) {
                if (!apartment?.id) errs.apartment = "Выберите серию";
                if (!construc?.id) errs.construc = "Выберите материал стен";
                if (!floor) errs.floor = "Укажите этаж";
                if (!totalFloor) errs.totalFloor = "Укажите всего этажей";
                if (!rooms) errs.rooms = "Укажите кол-во комнат";
                if (!year) errs.year = "Укажите год постройки";
                if (!rep?.id) errs.rep = "Выберите состояние ремонта";
                if (!heat?.id) errs.heat = "Выберите тип отопления";
                if (!furnit?.id) errs.furnit = "Выберите тип мебелирования";
            }

            if (isHouse) {
                if (!construc?.id) errs.construc = "Выберите материал стен";
                if (!areas?.trim() || Number(areas) <= 0)
                    errs.areas = "Укажите площадь участка (в сотках)";
                if (!rooms) errs.rooms = "Укажите количество комнат";
                if (!totalFloor) errs.totalFloor = "Укажите всего этажей";
                if (!year) errs.year = "Укажите год постройки";
                if (!rep?.id) errs.rep = "Выберите состояние ремонта";
                if (!heat?.id) errs.heat = "Выберите тип отопления";
                if (!furnit?.id) errs.furnit = "Выберите тип мебелирования";
            }

            if (isLand) {
                if (!communications?.trim()) errs.communications = "Опишите коммуникации";
                else if (communications.trim().length < 5)
                    errs.communications = "Коммуникации должно быть не меньше 5 символов";
                if (!purpose?.trim()) errs.purpose = "Укажите целевое назначение";
                else if (purpose.trim().length < 5)
                    errs.purpose = "Целевое назначение должно быть не меньше 5 символов";
            }
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    }


    function BackButton() {
        if (step === 1) return null;
        return (
            <button
                type="button"
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                className="w-full bg-white/40 text-black/70 border py-2 rounded-lg hover:bg-white/60 transition"
            >
                Назад
            </button>
        );
    }

    const progressPct = useMemo(
        () => (step / TOTAL_STEPS) * 100,
        [step]
    );

    function NextButton() {
        if (step >= TOTAL_STEPS) return null;
        return (
            <button
                type="button"
                onClick={() => {
                    if (validateStep(step)) setStep((s) => s + 1);
                }}
                className="w-full bg-black/60 text-white py-2 rounded-lg hover:bg-black transition"
            >
                Далее
            </button>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateStep(step)) return;
        setSubmitError("");
        setSubmitting(true);

        const rawPhone = phone.replace(/\D/g, "");
        const formattedPhone = `+${rawPhone}`;

        const buildPayload = () => {
            const base = {
                category_id: category?.id,
                title: title.trim(),
                district: district.trim(),
                address: address.trim(),
                description: description.trim(),
                price_usd: Number(price),
                area_total_m2: Number(area),
                contacts: formattedPhone,
            };

            switch (category.realty_type) {
                case "apartment":
                    return {
                        ...base,
                        floor: Number(floor),
                        floors_total: Number(totalFloor),
                        year_built: Number(year),
                        repair_type_id: rep?.id,
                        apartment_series_id: apartment?.id,
                        construction_type_id: construc?.id,
                        heating_id: heat?.id,
                        furniture_id: furnit?.id,
                    };
                case "commercial":
                    return {
                        ...base,
                        floor: Number(floor),
                        rooms: Number(rooms),
                        floors_total: Number(totalFloor),
                        year_built: Number(year),
                        repair_type_id: rep?.id,
                        apartment_series_id: apartment?.id,
                        construction_type_id: construc?.id,
                        heating_id: heat?.id,
                        furniture_id: furnit?.id,
                    };
                case "house":
                    return {
                        ...base,
                        land_area_sotka: Number(areas),
                        rooms: Number(rooms),
                        floors_total: Number(totalFloor),
                        year_built: Number(year),
                        repair_type_id: rep?.id,
                        construction_type_id: construc?.id,
                        heating_id: heat?.id,
                        furniture_id: furnit?.id,
                    };
                case "land":
                    return {
                        ...base,
                        communications: communications.trim(),
                        purpose: purpose.trim(),
                    };
                default:
                    return base;
            }
        };

        try {
            let currentToken = localStorage.getItem("access_token");
            const expiryDate = localStorage.getItem("expiry_date");
            const expiresInMs = expiryDate ? new Date(expiryDate).getTime() - Date.now() : null;

            if (expiresInMs !== null && expiresInMs <= 60 * 1000) {
                currentToken = await refreshAccessToken();
            }

            if (!currentToken) {
                setSubmitError("Сессия истекла. Войдите заново и попробуйте создать объявление.");
                return;
            }

            const payload = buildPayload();
            const response = await create(payload, currentToken);

            if (!response?.id) {
                setSubmitError("Сервер не вернул ID созданного объявления. Попробуйте снова.");
                return;
            }

            setId(response.id);
            setStep(3);

        } catch (error) {
            console.error("Ошибка при создании:", error);
            setSubmitError(getCreateErrorMessage(error));
        } finally {
            setSubmitting(false);
        }
    };

    const ErrorText = ({name}) =>
        errors[name] ? (
            <p className="text-xs text-red-600 mt-1">{errors[name]}</p>
        ) : null;

    return (
        <div
            style={{
                backgroundImage: `url(https://wallpaper.forfun.com/fetch/e6/e609adbf955ffed9970fd0045bbf8643.jpeg)`,
            }}
            className={"bg-cover relative z-20 bg-center min-h-[85vh]"}
        >
            <div className="container flex h-full justify-center py-5">
                <div
                    className="md:w-1/2 h-auto flex items-center justify-center rounded-2xl bg-white/45 backdrop-blur-md relative overflow-hidden">
                    <div className="flex flex-col items-center p-8 rounded-lg shadow-md w-full h-full">
                        <h2 className="md:text-2xl text-xl font-semibold mb-6 text-black/70">
                            Создать объявление
                        </h2>

                        {/* Прогресс */}
                        <div className="w-full h-2 bg-black/10 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-black/70 transition-all"
                                style={{width: `${progressPct}%`}}
                            />
                        </div>
                        <div className="flex items-center justify-between text-sm text-black/70 mb-4">
                            <span>Шаг {step} из {TOTAL_STEPS}</span>
                            <span className="font-medium pl-1">
                {step === 1 && "Основные данные"}
                                {step === 2 && "Характеристики"}
                                {step === 3 && "Медиа"}
              </span>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4 w-full">
                            {/* ШАГ 1 */}
                            {step === 1 && (
                                <>
                                    {/* category */}
                                    <div>
                                        <Listbox value={category} onChange={setCategory}>
                                            <div className="relative w-full">
                                                <Listbox.Button
                                                    className="text-left w-full text-black/70 bg-white/5 backdrop-blur-md border rounded-md px-3 py-2">
                                                    {category?.name || "Выберите категорию"}
                                                </Listbox.Button>
                                                <Listbox.Options
                                                    className="absolute mt-0.5 w-full rounded-md bg-white/50 backdrop-blur-md shadow-lg z-10">
                                                    {reference?.categories.map((cat) => (
                                                        <Listbox.Option
                                                            key={cat.id}
                                                            value={cat}
                                                            className="cursor-pointer px-3 py-2 hover:bg-white/70"
                                                        >
                                                            {cat.name}
                                                        </Listbox.Option>
                                                    ))}
                                                </Listbox.Options>
                                            </div>
                                        </Listbox>
                                        <ErrorText name="category"/>
                                    </div>
                                    {/* title */}
                                    <div>
                                        <label className="block text-black/70 text-sm mb-1">Название</label>
                                        <input
                                            value={title}
                                            placeholder="Светлая 2к квартира в центре..."
                                            onChange={(e) => setTitle(e.target.value)}
                                            type="text"
                                            className="w-full bg-white/35 text-black/70 border rounded-md px-3 py-2 focus:outline-none focus:shadow-md"
                                        />
                                        <ErrorText name="title"/>
                                    </div>


                                    {/* address */}
                                    <div className="w-full flex flex-col md:flex-row justify-between gap-5">
                                        <div className="w-full">
                                            <label className="block text-black/70 text-sm mb-1">Район</label>
                                            <input
                                                value={district}
                                                placeholder="Бишкек, Ленинский..."
                                                onChange={(e) => setDistrict(e.target.value)}
                                                type="text"
                                                className="w-full bg-white/35 text-black/70 border rounded-md px-3 py-2 focus:outline-none focus:shadow-md"
                                            />
                                            <ErrorText name="district"/>
                                        </div>
                                        <div className="w-full">
                                            <label className="block text-black/70 text-sm mb-1">Адрес</label>
                                            <input
                                                value={address}
                                                placeholder="ул. Ибраимова, 115..."
                                                onChange={(e) => setAddress(e.target.value)}
                                                type="text"
                                                className="w-full bg-white/35 text-black/70 border rounded-md px-3 py-2 focus:outline-none focus:shadow-md"
                                            />
                                            <ErrorText name="address"/>
                                        </div>
                                    </div>
                                    {/* description */}
                                    <div>
                                        <label className="block text-black/70 text-sm mb-1">Описание</label>
                                        <textarea
                                            value={description}
                                            placeholder="Отличная планировка, рядом парк..."
                                            onChange={(e) => setDescription(e.target.value)}
                                            className="w-full bg-white/35 text-black/70 border rounded-md px-3 py-2 focus:outline-none focus:shadow-md"
                                        />
                                        <ErrorText name="description"/>
                                    </div>


                                    {/* prices */}
                                    <div className="w-full flex flex-col md:flex-row justify-between gap-5">
                                        <div className="w-full">
                                            <label className="block text-black/70 text-sm mb-1">Цена $</label>
                                            <div className="relative w-full">
                                                <input
                                                    value={price}
                                                    onChange={(e) => setPrice(e.target.value)}
                                                    placeholder="85000"
                                                    type="number"
                                                    className="w-full bg-white/35 text-black/70 border rounded-md px-3 py-2 pr-12 focus:outline-none focus:shadow-md"
                                                />
                                                <span
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-black/50 text-sm">USD</span>
                                            </div>
                                            <ErrorText name="price"/>
                                        </div>
                                        <div className="w-full">
                                            <label className="block text-black/70 text-sm mb-1">Площадь м²</label>
                                            <div className="relative w-full">
                                                <input
                                                    value={area}
                                                    onChange={(e) => setArea(e.target.value)}
                                                    type="number"
                                                    placeholder="64.5"
                                                    className="w-full bg-white/35 text-black/70 border rounded-md px-3 py-2 pr-12 focus:outline-none focus:shadow-md"
                                                />
                                                <span
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-black/50 text-sm">m²</span>
                                            </div>
                                            <ErrorText name="area"/>
                                        </div>
                                    </div>

                                    {/* contacts */}
                                    <div>
                                        <label className="text-black/70 block text-sm mb-1">Контакты</label>
                                        <InputMask
                                            mask="+996 (000) 00-00-00"
                                            maskChar={null}
                                            formatChars={{0: "[0-9]"}}
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                        >
                                            {(inputProps) => (
                                                <input
                                                    {...inputProps}
                                                    type="tel"
                                                    className="text-black/70 bg-white/30 md:text-base text-sm w-full font-bold border p-2 rounded-lg focus:outline-none focus:shadow-md"
                                                    placeholder="+996 (___) __-__-__"
                                                />
                                            )}
                                        </InputMask>
                                        <ErrorText name="phone"/>
                                    </div>


                                    <NextButton/>
                                </>
                            )}
                            {/* ШАГ 2 */}
                            {step === 2 && (
                                <>
                                    {/* land only */}
                                    {isLand && (
                                        <>
                                            <div>
                                                <label
                                                    className="block text-black/70 text-sm mb-1">Коммуникация</label>
                                                <textarea
                                                    value={communications}
                                                    onChange={(e) => setCommunications(e.target.value)}
                                                    placeholder="Свет, вода, газ..."
                                                    className="w-full bg-white/35 text-black/70 border rounded-md px-3 py-2 focus:outline-none focus:shadow-md"
                                                />
                                                <ErrorText name="communications"/>
                                            </div>
                                            <div>
                                                <label className="block text-black/70 text-sm mb-1">Целевое
                                                    назначение</label>
                                                <textarea
                                                    value={purpose}
                                                    onChange={(e) => setPurpose(e.target.value)}
                                                    placeholder="ИЖС, коммерция..."
                                                    className="w-full bg-white/35 text-black/70 border rounded-md px-3 py-2 focus:outline-none focus:shadow-md"
                                                />
                                                <ErrorText name="purpose"/>
                                            </div>
                                        </>
                                    )}


                                    {/* not land */}
                                    {!isLand && (
                                        <>
                                            {/* construction */}
                                            <div>
                                                <Listbox value={construc} onChange={setConstruc}>
                                                    <div className="relative w-full">
                                                        <Listbox.Button
                                                            className="text-left w-full text-black/70 bg-white/5 backdrop-blur-md border rounded-md px-3 py-2">
                                                            {construc?.name || "Выберите Материал стен"}
                                                        </Listbox.Button>
                                                        <Listbox.Options
                                                            className="absolute mt-0.5 w-full rounded-md bg-white/80 backdrop-blur-md shadow-lg z-10
                                                                       max-h-96 overflow-y-auto"
                                                        >
                                                            {reference?.construction_types.map((cat) => (
                                                                <Listbox.Option
                                                                    key={cat.id}
                                                                    value={cat}
                                                                    className="cursor-pointer px-3 py-2 hover:bg-white"
                                                                >
                                                                    {cat.name}
                                                                </Listbox.Option>
                                                            ))}
                                                        </Listbox.Options>
                                                    </div>
                                                </Listbox>
                                                <ErrorText name="construc"/>
                                            </div>
                                            {/* heating */}
                                            <div>
                                                <Listbox value={heat} onChange={setHeat}>
                                                    <div className="relative w-full">
                                                        <Listbox.Button
                                                            className="text-left w-full text-black/70 bg-white/50 backdrop-blur-md border rounded-md px-3 py-2">
                                                            {heat?.name || "Выберите Тип отопления"}
                                                        </Listbox.Button>
                                                        <Listbox.Options
                                                            className="absolute mt-0.5 w-full rounded-md overflow-auto bg-white/80 backdrop-blur-md shadow-lg z-10">
                                                            {reference?.heating_types.map((cat) => (
                                                                <Listbox.Option
                                                                    key={cat.id}
                                                                    value={cat}
                                                                    className="cursor-pointer px-3 py-2 hover:bg-white"
                                                                >
                                                                    {cat.name}
                                                                </Listbox.Option>
                                                            ))}
                                                        </Listbox.Options>
                                                    </div>
                                                </Listbox>
                                                <ErrorText name="heat"/>
                                            </div>
                                            {!isHouse && !isLand && (
                                                <div>
                                                    <Listbox value={apartment} onChange={setApartment}>
                                                        <div className="relative w-full">
                                                            <Listbox.Button
                                                                className="text-left w-full text-black/70 bg-white/50 backdrop-blur-md border rounded-md px-3 py-2">
                                                                {apartment?.name || "Выберите Серию"}
                                                            </Listbox.Button>
                                                            <Listbox.Options
                                                                className="absolute mt-0.5 w-full rounded-md bg-white/80 backdrop-blur-md shadow-lg z-10
                                                                           max-h-64 overflow-y-auto"
                                                            >
                                                                {reference?.apartment_series.map((cat) => (
                                                                    <Listbox.Option
                                                                        key={cat.id}
                                                                        value={cat}
                                                                        className="cursor-pointer px-3 py-2 hover:bg-white"
                                                                    >
                                                                        {cat.name}
                                                                    </Listbox.Option>
                                                                ))}
                                                            </Listbox.Options>
                                                        </div>
                                                    </Listbox>
                                                    <ErrorText name="apartment"/>
                                                </div>
                                            )}
                                            {/* furniture */}
                                            <div>
                                                <Listbox value={furnit} onChange={setFurnit}>
                                                    <div className="relative w-full">
                                                        <Listbox.Button
                                                            className="text-left w-full text-black/70 bg-white/50 backdrop-blur-md border rounded-md px-3 py-2">
                                                            {furnit?.name || "Выберите Тип мебелирования"}
                                                        </Listbox.Button>
                                                        <Listbox.Options
                                                            className="absolute mt-0.5 w-full rounded-md bg-white/80 backdrop-blur-md shadow-lg z-10
                                                                        max-h-52 overflow-y-auto"
                                                        >
                                                            {reference?.furniture_types.map((cat) => (
                                                                <Listbox.Option
                                                                    key={cat.id}
                                                                    value={cat}
                                                                    className="cursor-pointer px-3 py-2 hover:bg-white"
                                                                >
                                                                    {cat.name}
                                                                </Listbox.Option>
                                                            ))}
                                                        </Listbox.Options>
                                                    </div>
                                                </Listbox>
                                                <ErrorText name="furnit"/>
                                            </div>
                                            {/* repair */}
                                            <div>
                                                <Listbox value={rep} onChange={setRep}>
                                                    <div className="relative w-full">
                                                        <Listbox.Button
                                                            className="text-left w-full text-black/70 bg-white/50 backdrop-blur-md border rounded-md px-3 py-2">
                                                            {rep?.name || "Выберите Состояние ремонта"}
                                                        </Listbox.Button>
                                                        <Listbox.Options
                                                            className="absolute mt-0.5 w-full rounded-md bg-white/80 backdrop-blur-md shadow-lg z-10
                                                                        max-h-40 overflow-y-auto"
                                                        >
                                                            {reference?.repair_types.map((cat) => (
                                                                <Listbox.Option
                                                                    key={cat.id}
                                                                    value={cat}
                                                                    className="cursor-pointer px-3 py-2 hover:bg-white"
                                                                >
                                                                    {cat.name}
                                                                </Listbox.Option>
                                                            ))}
                                                        </Listbox.Options>
                                                    </div>
                                                </Listbox>
                                                <ErrorText name="rep"/>
                                            </div>
                                            {/* floor & rooms */}
                                            <div className="w-full flex flex-col md:flex-row justify-between gap-5">
                                                {!isApartment && (
                                                    <div className="w-full">
                                                        <label
                                                            className="block text-black/70 text-sm mb-1">Комнаты</label>
                                                        <input
                                                            value={rooms}
                                                            onChange={(e) => setRooms(e.target.value)}
                                                            type="number"
                                                            placeholder={"2"}
                                                            className="w-full bg-white/35 text-black/70 border rounded-md px-3 py-2 focus:outline-none focus:shadow-md"
                                                        />
                                                        <ErrorText name="rooms"/>
                                                    </div>
                                                )}


                                                {!isHouse && (
                                                    <div className="w-full">
                                                        <label
                                                            className="block text-black/70 text-sm mb-1">Этаж</label>
                                                        <input
                                                            value={floor}
                                                            placeholder={"5"}
                                                            onChange={(e) => setFloor(e.target.value)}
                                                            type="number"
                                                            className="w-full bg-white/35 text-black/70 border rounded-md px-3 py-2 focus:outline-none focus:shadow-md"
                                                        />
                                                        <ErrorText name="floor"/>
                                                    </div>
                                                )}


                                                <div className="w-full">
                                                    <label className="block text-black/70 text-sm mb-1">Всего
                                                        этажей</label>
                                                    <input
                                                        value={totalFloor}
                                                        placeholder={"10"}
                                                        onChange={(e) => setTotalFloor(e.target.value)}
                                                        type="number"
                                                        className="w-full bg-white/35 text-black/70 border rounded-md px-3 py-2 focus:outline-none focus:shadow-md"
                                                    />
                                                    <ErrorText name="totalFloor"/>
                                                </div>
                                                <div className="w-full">
                                                    <label className="block text-black/70 text-sm mb-1">Год
                                                        постройки</label>
                                                    <input
                                                        value={year}
                                                        placeholder={"2012"}
                                                        onChange={(e) => setYear(e.target.value)}
                                                        type="number"
                                                        className="w-full bg-white/35 text-black/70 border rounded-md px-3 py-2 focus:outline-none focus:shadow-md"
                                                    />
                                                    <ErrorText name="year"/>
                                                </div>
                                                {isHouse && (
                                                    <div className="w-full">
                                                        <label className="block text-black/70 text-sm mb-1">Площадь
                                                            участка</label>
                                                        <div className="relative w-full">
                                                            <input
                                                                value={areas}
                                                                onChange={(e) => setAreas(e.target.value)}
                                                                type="text"
                                                                placeholder="10"
                                                                className="w-full bg-white/35 text-black/70 border rounded-md px-3 py-2 pr-12 focus:outline-none focus:shadow-md"
                                                            />
                                                            <span
                                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-black/50 text-sm">сотка</span>
                                                        </div>
                                                        <ErrorText name="areas"/>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <BackButton step={step} setStep={setStep}/>
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            className="w-full bg-black/60 text-white py-2 rounded-lg hover:bg-black transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {submitting ? "Создание..." : "Далее"}
                                        </button>
                                    </div>
                                    {submitError && (
                                        <p className="text-sm text-red-600 mt-2">{submitError}</p>
                                    )}
                                </>
                            )}

                            {step === 3 && (
                                <UploadMedia id={id} step={step} setStep={setStep}/>
                            )}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateHouse;
